/**
 * Security-related constants for Quote Tool applications.
 * These constants define security policies and validation rules for return URL functionality.
 */

/**
 * List of dangerous URL protocols that should always be blocked.
 * These protocols can be used for XSS attacks or other security vulnerabilities.
 *
 * @example
 * // Blocked URLs:
 * - javascript:alert('XSS')
 * - data:text/html,<script>alert('XSS')</script>
 * - file:///etc/passwd
 * - vbscript:msgbox("XSS")
 */
export const DANGEROUS_URL_PROTOCOLS: readonly string[] = ['javascript:', 'data:', 'file:', 'vbscript:'] as const;

/**
 * Creates an array of regex patterns for allowed return URL domains.
 * These patterns match trusted portal domains across different environments.
 *
 * @param httpsOnly - If true, only HTTPS protocol is allowed. If false, both HTTP and HTTPS are allowed.
 * @returns Array of regex patterns for validating return URLs
 *
 * @example
 * // For production (HTTPS only):
 * const patterns = createAllowedReturnUrlPatterns(true);
 *
 * // For development/staging (HTTP and HTTPS):
 * const patterns = createAllowedReturnUrlPatterns(false);
 *
 * @security
 * - Only add domains that are owned and controlled by KLG/Kaplan/Alpadia
 * - Patterns should be as specific as possible while allowing for subdomains
 */
export function createAllowedReturnUrlPatterns(httpsOnly = false): readonly RegExp[] {
  const protocol = httpsOnly ? 'https' : 'https?';

  return [
    new RegExp(`^${protocol}://([a-z0-9-]+\\.)*klgintdc\\.com(/.*)?$`, 'i'), // Internal KLG domains (all subdomains)
    new RegExp(`^${protocol}://(www\\.)?kaplaninternational\\.com(/.*)?$`, 'i'), // Kaplan International domains (root or www only)
    new RegExp(`^${protocol}://(www\\.)?alpadia\\.com(/.*)?$`, 'i'), // Alpadia domains (root or www only)
    new RegExp(`^${protocol}://(www\\.)?alpadia\\.ch(/.*)?$`, 'i'), // Alpadia Switzerland domains (root or www only)
  ] as const;
}
